import { httpClient } from '../lib/httpClient';
import type { KYCReview, KYCReviewComment, ReviewDecision, CommentSeverity, RejectionCategory } from '../types/complianceWorkflow';

/** Standard API response wrapper returned by the backend */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Unwrap an Axios response that may be a raw `T` or an `ApiResponse<T>`.
 * Axios already resolves `.data` from the HTTP body, but our backend
 * sometimes wraps the payload in `{ success, data }`.
 */
function unwrap<T>(axiosData: ApiResponse<T> | T): T {
  if (
    axiosData !== null &&
    typeof axiosData === 'object' &&
    'success' in (axiosData as Record<string, unknown>) &&
    'data' in (axiosData as Record<string, unknown>)
  ) {
    return (axiosData as ApiResponse<T>).data;
  }
  return axiosData as T;
}

// Get latest review for a KYC profile (advisor view)
export async function getReviewByProfile(kycProfileId: string): Promise<(KYCReview & { comments?: KYCReviewComment[] }) | null> {
  const response = await httpClient.get<ApiResponse<(KYCReview & { comments?: KYCReviewComment[] }) | null>>(`/api/kyc/reviews/by-profile/${kycProfileId}`);
  return unwrap(response.data) ?? null;
}

// Review Queue
export async function getReviewQueue(params?: { status?: string; limit?: number; offset?: number }) {
  return httpClient.get<{ items: KYCReview[]; total: number }>('/api/kyc/reviews/queue', { params });
}

// Assign review
export async function assignReview(kycProfileId: string): Promise<KYCReview> {
  const response = await httpClient.post<ApiResponse<KYCReview>>('/api/kyc/reviews/assign', { kyc_profile_id: kycProfileId });
  return unwrap(response.data);
}

// Get review details
export async function getReview(reviewId: string): Promise<KYCReview & { comments?: KYCReviewComment[] }> {
  const response = await httpClient.get<ApiResponse<KYCReview & { comments?: KYCReviewComment[] }>>(`/api/kyc/reviews/${reviewId}`);
  return unwrap(response.data);
}

// Mark section as reviewed
export async function markSectionReviewed(reviewId: string, sectionId: string) {
  const response = await httpClient.put<ApiResponse<KYCReview>>(`/api/kyc/reviews/${reviewId}/sections/${sectionId}/reviewed`);
  return unwrap(response.data);
}

// Submit decision
export async function submitDecision(reviewId: string, data: { decision: ReviewDecision; notes: string; rejection_category?: RejectionCategory }) {
  const response = await httpClient.post<ApiResponse<KYCReview>>(`/api/kyc/reviews/${reviewId}/decision`, data);
  return unwrap(response.data);
}

// Comments
export async function getComments(reviewId: string): Promise<KYCReviewComment[]> {
  const response = await httpClient.get<ApiResponse<KYCReviewComment[]>>(`/api/kyc/reviews/${reviewId}/comments`);
  return unwrap(response.data);
}

export async function addComment(reviewId: string, data: { section_id: string; field_path?: string; comment: string; severity: CommentSeverity }): Promise<KYCReviewComment> {
  const response = await httpClient.post<ApiResponse<KYCReviewComment>>(`/api/kyc/reviews/${reviewId}/comments`, data);
  return unwrap(response.data);
}

export async function resolveComment(reviewId: string, commentId: string) {
  const response = await httpClient.put<ApiResponse<KYCReviewComment>>(`/api/kyc/reviews/${reviewId}/comments/${commentId}/resolve`);
  return unwrap(response.data);
}
